import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { loginByToken, logoutMember } from '../services/api';
import { buildAuthProfileFromMember, parseWsAuthResult } from '../utils/authProfile';
import { clearWsToken } from '../utils/wsToken';

const AuthContext = createContext(null);
const STORAGE_KEY = 'taogo-member-user';
const TOKEN_KEY = 'taogo-member-token';

function hasMemberId(user) {
  return user?.id != null && user?.id !== '';
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [hydrating, setHydrating] = useState(false);
  const authSessionRef = useRef(0);

  const persistUser = useCallback((profile) => {
    setUser(profile);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
  }, []);

  const applyToken = useCallback((authToken) => {
    if (!authToken) {
      return;
    }
    setToken(authToken);
    localStorage.setItem(TOKEN_KEY, authToken);
  }, []);

  const hydrateFromToken = useCallback(
    async (authToken, fallbackProfile = {}, sessionId = authSessionRef.current) => {
      if (!authToken) {
        return fallbackProfile;
      }
      try {
        const member = await loginByToken(authToken);
        if (authSessionRef.current !== sessionId) {
          return fallbackProfile;
        }
        if (localStorage.getItem(TOKEN_KEY) !== authToken) {
          return fallbackProfile;
        }
        const profile = buildAuthProfileFromMember(member, fallbackProfile);
        if (hasMemberId(profile)) {
          persistUser(profile);
        }
        return profile;
      } catch (error) {
        console.warn('hydrateFromToken failed:', error);
        return fallbackProfile;
      }
    },
    [persistUser],
  );

  useEffect(() => {
    let cancelled = false;

    const initAuth = async () => {
      const sessionAtStart = authSessionRef.current;
      const tokenAtStart = localStorage.getItem(TOKEN_KEY);

      try {
        const userRaw = localStorage.getItem(STORAGE_KEY);
        const tokenRaw = localStorage.getItem(TOKEN_KEY);
        const parsedUser = userRaw ? JSON.parse(userRaw) : null;

        if (tokenRaw) {
          setHydrating(true);
          applyToken(tokenRaw);
          const profile = await hydrateFromToken(tokenRaw, parsedUser ?? {}, sessionAtStart);
          if (!cancelled && authSessionRef.current === sessionAtStart && hasMemberId(profile)) {
            persistUser(profile);
          }
        } else if (parsedUser?.phone) {
          if (!cancelled && authSessionRef.current === sessionAtStart) {
            setUser(parsedUser);
          }
        }
      } catch (error) {
        console.warn('Failed to load auth info', error);
        if (
          !cancelled &&
          authSessionRef.current === sessionAtStart &&
          localStorage.getItem(TOKEN_KEY) === tokenAtStart
        ) {
          localStorage.removeItem(STORAGE_KEY);
          localStorage.removeItem(TOKEN_KEY);
        }
      } finally {
        if (!cancelled) {
          setHydrating(false);
          setInitialized(true);
        }
      }
    };

    initAuth();

    return () => {
      cancelled = true;
    };
  }, [applyToken, hydrateFromToken, persistUser]);

  /** 有 token 但 profile 缺 id 时再拉一次（避免首页无 ID / 优惠券 / 二维码） */
  useEffect(() => {
    if (!initialized || !token || hasMemberId(user)) {
      return undefined;
    }
    let cancelled = false;
    const sessionId = authSessionRef.current;
    setHydrating(true);
    (async () => {
      await hydrateFromToken(token, user ?? {}, sessionId);
      if (!cancelled) {
        setHydrating(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [initialized, token, user, hydrateFromToken]);

  const login = useCallback(
    (profile, authToken = null) => {
      authSessionRef.current += 1;
      persistUser(profile);
      if (authToken) {
        applyToken(authToken);
        if (!hasMemberId(profile)) {
          const sessionId = authSessionRef.current;
          hydrateFromToken(authToken, profile, sessionId).catch(() => {});
        }
      }
    },
    [applyToken, hydrateFromToken, persistUser],
  );

  /**
   * WhatsApp 注册/登录：接口已返回完整会员时立即落登录态；缺 id 时才用 token 补拉。
   */
  const loginFromWsApiResult = useCallback(
    async (apiResult, fallback = {}) => {
      authSessionRef.current += 1;
      const sessionId = authSessionRef.current;
      const { profile: initialProfile, token: authToken } = parseWsAuthResult(apiResult, fallback);

      if (hasMemberId(initialProfile)) {
        persistUser(initialProfile);
        if (authToken) {
          applyToken(authToken);
          setHydrating(true);
          try {
            // 先把 token 验证和最新会员资料取完，再允许注册页跳转到 Home。
            // initialProfile 已先落盘，因此即使接口短暂失败也不会丢失登录态。
            return await hydrateFromToken(authToken, initialProfile, sessionId);
          } finally {
            if (authSessionRef.current === sessionId) {
              setHydrating(false);
            }
          }
        }
        return initialProfile;
      }

      if (!authToken) {
        return initialProfile;
      }

      applyToken(authToken);
      setHydrating(true);
      try {
        const profile = await hydrateFromToken(authToken, initialProfile, sessionId);
        return profile;
      } finally {
        if (authSessionRef.current === sessionId) {
          setHydrating(false);
        }
      }
    },
    [applyToken, hydrateFromToken, persistUser],
  );

  /** 使用长期 token 重新拉取当前会员资料；供注册后页面补齐首屏数据。 */
  const refreshSession = useCallback(async () => {
    const authToken = token || localStorage.getItem(TOKEN_KEY);
    if (!authToken) {
      return user;
    }
    return hydrateFromToken(authToken, user ?? {}, authSessionRef.current);
  }, [hydrateFromToken, token, user]);

  const logout = useCallback(async () => {
    authSessionRef.current += 1;
    const currentToken = token;

    setUser(null);
    setToken(null);
    setHydrating(false);
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(TOKEN_KEY);
    clearWsToken();

    if (currentToken) {
      try {
        await logoutMember(currentToken);
      } catch (error) {
        console.warn('Logout API call failed:', error);
      }
    }
  }, [token]);

  const logoutLocalOnly = useCallback(() => {
    authSessionRef.current += 1;
    setUser(null);
    setToken(null);
    setHydrating(false);
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(TOKEN_KEY);
  }, []);

  const value = useMemo(
    () => ({
      user,
      token,
      login,
      loginFromWsApiResult,
      refreshSession,
      logout,
      logoutLocalOnly,
      initialized,
      hydrating,
      isAuthenticated: hasMemberId(user),
      isSessionReady: initialized && !hydrating && (hasMemberId(user) || !token),
    }),
    [hydrating, initialized, login, loginFromWsApiResult, logout, logoutLocalOnly, refreshSession, token, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
